import { useCallback, useEffect, useRef, useState } from 'react';
import { LocalAudioTrack } from 'twilio-video';
import { useVideo } from '../useVideo';
import { useIsTrackEnabled } from '../useIsTrackEnabled';
import { LOCAL_AUDIO_ENABLED_KEY } from '../../constants';

export function useLocalAudioToggle() {
  const { localTracks } = useVideo();
  const audioTrack = localTracks.find((track) => track.kind === 'audio') as LocalAudioTrack;
  const isEnabled = useIsTrackEnabled(audioTrack);
  const [isTogglingAudio, setIsTogglingAudio] = useState(false);
  const toggleTimeoutRef = useRef<number>();

  useEffect(
    () => () => {
      if (toggleTimeoutRef.current) {
        window.clearTimeout(toggleTimeoutRef.current);
      }
    },
    [],
  );

  const toggleAudioEnabled = useCallback(() => {
    if (!audioTrack || isTogglingAudio) {
      return;
    }

    setIsTogglingAudio(true);

    if (audioTrack.isEnabled) {
      audioTrack.disable();
      window.localStorage.setItem(LOCAL_AUDIO_ENABLED_KEY, 'false');
    } else {
      audioTrack.enable();
      window.localStorage.setItem(LOCAL_AUDIO_ENABLED_KEY, 'true');
    }

    if (toggleTimeoutRef.current) {
      window.clearTimeout(toggleTimeoutRef.current);
    }
    toggleTimeoutRef.current = window.setTimeout(() => {
      setIsTogglingAudio(false);
    }, 500);
  }, [audioTrack, isTogglingAudio]);

  return [isEnabled, toggleAudioEnabled, isTogglingAudio] as const;
}
